import { API_BASE_URL } from '@/lib/apiBase'
import { clearAuth, getAccessToken, getRefreshToken, setAuth } from '@/lib/authToken'
import { SURFACE_ENTRY_URL } from '@/lib/viewMode'
import { isNetworkError, reportNoAnswer, reportReachable } from '@/lib/connectivity'

/**
 * Builds request headers with the bearer token attached when signed in.
 * Shared by every api client so auth is applied consistently.
 */
export function authHeaders(extra?: HeadersInit): HeadersInit {
  const token = getAccessToken()
  // Attach the selected tenant so backend per-tenant enforcement works. Mirrors
  // the localStorage key TenantProvider persists. An explicit header in `extra`
  // still wins.
  const tenantId = localStorage.getItem('cgt_selected_tenant')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    ...extra,
  }
}

/** True while the mobile surface (the `/m` html entry) is the one running. */
function onMobileSurface(): boolean {
  const p = window.location.pathname
  // Exact `/m` or a `/m/` prefix only — `/my-steps` must not match.
  return p === '/m' || p.startsWith('/m/')
}

/**
 * On a 401 the session is dead — clear tokens and bounce to the login screen.
 * Call this before throwing so a stale token can't trap the user.
 *
 * The target depends on which surface is running. Sending a mobile user to
 * `/login` would load the desktop bundle, and `/m/login` would need a CDN
 * rewrite that does not exist yet, so mobile goes to the mobile entry object.
 * `clearAuth()` has already run, so the mobile guard renders its own login
 * screen on arrival.
 *
 * `SURFACE_ENTRY_URL` rather than a literal `/m/`: on CloudFront that path is
 * not resolvable and falls back to the desktop app. Both are compared against
 * the surface, not the URL, so an already-correct location does not reload.
 */
export function handleUnauthorized(status: number): void {
  if (status === 401) {
    clearAuth()
    if (onMobileSurface()) {
      const p = window.location.pathname
      // Both spellings of the entry count as "already there", or a 401 raised
      // on the home screen would reload the page instead of doing nothing.
      if (p !== '/m/' && p !== SURFACE_ENTRY_URL['/m/']) {
        window.location.assign(SURFACE_ENTRY_URL['/m/'])
      }
      return
    }
    // The SSO pages run signed out; a stale token's 401 must not navigate away mid-flow.
    if (!['/login', '/sso', '/auth/callback'].includes(window.location.pathname)) {
      window.location.assign('/login')
    }
  }
}

/** The one route a 401 must never be retried against — see `authFetch`. */
const REFRESH_PATH = '/auth/refresh'

/**
 * The renewal in flight, shared by every caller that hits a 401 while it runs.
 *
 * Single-flight is not an optimisation here. `/auth/refresh` is rate-limited
 * (20/min) and Cognito rotates the refresh token on use, so a page that fires
 * four requests and gets four 401s would, without this, spend three of its
 * exchanges on tokens the first one had already invalidated — and the last
 * writer would win with a stale value.
 */
let inflightRenewal: Promise<string | null> | null = null

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

/**
 * Exchange the refresh token for a new access token, or null when that is not
 * possible. Never throws: every failure path means "the session is over", which
 * the caller expresses by returning the original 401.
 *
 * Deliberately a bare `fetch` rather than `authApi.refresh`: that helper runs
 * every non-ok response through `handleUnauthorized`, so a failed renewal would
 * clear tokens and redirect from inside this function instead of letting the
 * original caller's error handling do it — and `authApi` imports this module,
 * so calling back into it would be a cycle.
 */
async function renewAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  // No refresh token means there is nothing to renew with. Returning early
  // keeps a signed-out tab from spending a request to learn that.
  if (!refreshToken) return null

  try {
    const res = await fetch(`${API_BASE_URL}${REFRESH_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) return null

    const tokens = (await res.json()) as { accessToken?: string; refreshToken?: string }
    if (!tokens.accessToken) return null

    // Persist the rotated refresh token too, or the next renewal presents one
    // Cognito has already retired.
    setAuth({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken })
    return tokens.accessToken
  } catch {
    // Offline or DNS failure. Indistinguishable from a dead session here, and
    // the caller's 401 handling is the right place to decide.
    return null
  }
}

function renewOnce(): Promise<string | null> {
  if (!inflightRenewal) {
    inflightRenewal = renewAccessToken().finally(() => {
      inflightRenewal = null
    })
  }
  return inflightRenewal
}

/**
 * `fetch` with the bearer token attached, which renews once and replays the
 * request when the token has merely lapsed.
 *
 * Why this exists: the Cognito access token lives 60 minutes while the refresh
 * token is good for 30 days, and every api client treats a 401 as a dead
 * session. Without renewal an actively-working user is signed out on the hour,
 * mid-form, holding a credential that would still have worked.
 *
 * The contract each caller relies on:
 *   - the caller's own headers survive the replay, including `X-Tenant-Id`;
 *     only `Authorization` is overwritten, and it is applied last so it wins
 *     even against a caller-supplied one
 *   - a failed renewal returns the ORIGINAL 401, so the caller's existing
 *     `handleUnauthorized` ends the session exactly as it does today
 *   - the refresh route is never retried against itself, which would loop
 *
 * Note this does NOT touch the idle clock, and must not: idle activity is
 * observed from DOM events, so a renewal fired by a background poll cannot make
 * a walked-away session look busy.
 */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await observedFetch(input, { ...init, headers: authHeaders(init?.headers) })
  if (res.status !== 401) return res

  // A 401 from the refresh route itself is the end of the session, not an
  // invitation to refresh again.
  if (urlOf(input).includes(REFRESH_PATH)) return res

  const token = await renewOnce()
  if (!token) return res

  return observedFetch(input, {
    ...init,
    headers: { ...authHeaders(init?.headers), Authorization: `Bearer ${token}` },
  })
}

/**
 * `fetch` that reports whether the server answered (`src/lib/connectivity.ts`):
 * any response at all means reachable, a thrown fetch means maybe not (it is
 * confirmed there first). The error is rethrown unchanged, so no caller sees
 * a difference.
 */
async function observedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    const res = await fetch(input, init)
    reportReachable(true)
    return res
  } catch (err) {
    if (isNetworkError(err)) reportNoAnswer()
    throw err
  }
}
